import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';

interface DownloadBannerContextValue {
    os: 'android' | 'ios' | 'other' | null;
    isBannerVisible: boolean;
    dismissBanner: () => void;
}

const DownloadBannerContext = createContext<DownloadBannerContextValue | undefined>(undefined);

export function useDownloadBanner(): DownloadBannerContextValue {
    const context = useContext(DownloadBannerContext);
    if (!context) {
        throw new Error('useDownloadBanner must be used within a DownloadBannerProvider');
    }
    return context;
}

interface DownloadBannerProviderProps {
    children: ReactNode;
    /** Time in milliseconds before the banner should appear */
    delay?: number;
}

export const DownloadBannerProvider: React.FC<DownloadBannerProviderProps> = ({
    children,
    delay = 3000, // Default delay of 3 seconds
}) => {
    const [os, setOs] = useState<'android' | 'ios' | 'other' | null>(null);
    const [isBannerVisible, setIsBannerVisible] = useState(false);

    useEffect(() => {
        // Detect OS
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        if (/android/i.test(userAgent)) {
            setOs('android');
        } else if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
            setOs('ios');
        } else {
            setOs('other');
        }
    }, []);

    useEffect(() => {
        // Check if banner was previously dismissed
        const dismissed = localStorage.getItem('downloadBannerDismissed') === 'true';
        // if (!dismissed && (os === 'ios' || os === 'android')) {
        if (!dismissed && (os === 'android')) {
            const timer = setTimeout(() => {
                setIsBannerVisible(true);
            }, delay);

            return () => clearTimeout(timer);
        }
    }, [os, delay]);

    const dismissBanner = () => {
        setIsBannerVisible(false);
        localStorage.setItem('downloadBannerDismissed', 'true');
    };

    const value: DownloadBannerContextValue = {
        os,
        isBannerVisible,
        dismissBanner,
    };

    return (
        <DownloadBannerContext.Provider value={value}>
            {children}
        </DownloadBannerContext.Provider>
    );
};